import type { UrlSyncedQuery } from "@v2/features/analytics/utils/query";
import type { SelectedTimePeriod } from "schemas/utils/analytics";

import { DEFAULT_FILTERS } from "@v2/features/analytics/constants";
import { generateDefaultUrlSyncedQuery } from "@v2/features/analytics/utils/query";
import { routes } from "@v2/utils/router";
import { generatePath } from "react-router-dom";
import { compressObjectToLzString } from "replo-utils/lib/lzString";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const URL_SYNCED_QUERY_URL_PARAM = "query";

export function generateDeepDiveNavigationPathFromUrl({
  url,
  urlHosts,
  path,
  workspaceId,
  projectId,
  selectedTimePeriod,
}: {
  url: string | null;
  urlHosts: string[];
  path: string;
  workspaceId: string;
  selectedTimePeriod: SelectedTimePeriod;
  projectId: string | null;
}) {
  const urlSyncedQuery = generateDefaultUrlSyncedQuery({
    urlHosts,
    overrides: {
      selectedTimePeriod,
      filters: {
        ...DEFAULT_FILTERS,
        urlPath: [
          {
            value: [path ?? ""],
            operator: "equals",
          },
        ],
      },
    },
  });

  if (!url) {
    return { urlSyncedQuery, deepDivePathWithCompressedUrlSyncedQuery: null };
  }

  let deepDivePath: string;

  if (projectId) {
    deepDivePath = generatePath(routes.shops.analytics.pageDetails, {
      projectId,
    });
  } else {
    deepDivePath = generatePath(routes.analytics.pageDetails, {
      workspaceId,
    });
  }

  const urlHost = new URL(url).host;
  urlSyncedQuery.urlHosts = [urlHost];

  const deepDivePathWithCompressedUrlSyncedQuery =
    generatePathWithCompressedUrlSyncedQuery({
      path: deepDivePath,
      urlSyncedQueryConfig: {
        type: "decompressed",
        urlSyncedQuery: urlSyncedQuery,
      },
    });

  return { urlSyncedQuery, deepDivePathWithCompressedUrlSyncedQuery };
}

/**
 * Should be used when navigating to the overview page or deep dive page.
 *
 * Given a path and a urlSyncedQueryConfig, it returns the path with the compressed config's
 * urlSyncedQuery appended to the path with ?query=''.
 *
 * The config's type is either:
 * 1) compressed: we already have the compressed string (i.e. we extracted the ?query='' param and are
 * redirecting to a new analytics path -> this happens in the DashboardMenuItems.tsx)
 *
 * 2) decompressed: we have the urlSyncedQuery as an object, which we now need to compress & append to the path. This
 * happens e.g. in AnalyticsMenuPane, where we manually construct a default urlSyncedQuery, which we have as an object,
 * and we now want to redirect to the deep dive page.
 *
 * @author Max 2024-12-16
 */
export function generatePathWithCompressedUrlSyncedQuery({
  path,
  urlSyncedQueryConfig,
}: {
  path: string | null;
  urlSyncedQueryConfig:
    | { type: "compressed"; urlSyncedQuery: string | null }
    | {
        type: "decompressed";
        urlSyncedQuery: UrlSyncedQuery;
      };
}) {
  if (!path) {
    return "";
  }

  return exhaustiveSwitch(urlSyncedQueryConfig)({
    compressed: ({ urlSyncedQuery }) => {
      return urlSyncedQuery
        ? `${path}?${URL_SYNCED_QUERY_URL_PARAM}=${urlSyncedQuery}`
        : path;
    },
    decompressed: ({ urlSyncedQuery }) => {
      if (!urlSyncedQuery) {
        return path;
      }

      const compressedUrlSyncedQuery = compressObjectToLzString(urlSyncedQuery);
      return `${path}?${URL_SYNCED_QUERY_URL_PARAM}=${compressedUrlSyncedQuery}`;
    },
  });
}
